import { combineReducers } from 'redux';

import { ActionWithOptionalPayload } from '../typings';
import * as constants from './constants';
import { SessionAction, SessionTypeKeys } from './types';
import i18nService, { LanguagesDefinition } from '../../services/i18nService';

export interface UserObject {
    roles?: any[];
    firstName?: string;
    lastName?: string;
    userType?: string;
    id?: number;
    isImpersonated?: boolean;
    isStudent?: boolean;
    hasExtendedAccess?: boolean;
    privileges?: string[];
    email?: string;
    userName?: string;
    lastLogin?: string;
    customPrivileges?: CustomPrivileges[];
    // Access groups (org units) the user is a member of, from DescribeLoggedInUser. Content can be
    // shared with one of these instead of with the user directly.
    effectiveOrgUnitIds?: number[];
    permissions?: string[];
    impersonationType?: 'VIEW_ONLY' | 'OPERATE_ONLY';
    hasEventLogAccess?: boolean;
}

export interface SessionInfo {
    signedWebSocketUrl?: string;
    csrfToken?: string;
}

export type MarketplaceProperties = {
    [key: string]: any;
}

export type SessionState = {
    readonly language: LanguagesDefinition;
    readonly user?: UserObject;
    readonly sessionInfo: SessionInfo;
    readonly marketplaceProperties: MarketplaceProperties;
    readonly originalUser?: UserObject | null;
    readonly impersonationType?: 'VIEW_ONLY' | 'OPERATE_ONLY' | null;
};

export type ExtendedSessionState = SessionState & {
    originalUser?: UserObject;
    impersonationType?: 'VIEW_ONLY' | 'OPERATE_ONLY';
};

const initialLanguage: LanguagesDefinition = i18nService?.getInitialLanguage();

export const initialState: SessionState = {
    language: initialLanguage,
    user: {},
    sessionInfo: {},
    marketplaceProperties: [],
};

export const sessionReducer = combineReducers<SessionState, SessionAction>({
    user: (state = initialState.user, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case SessionTypeKeys.SET_USER_INFO:
            return action.payload;
        case SessionTypeKeys.SET_SESSION_DATA: {
            const { originalUser, ...userData } = action.payload;
            return {
                ...state,
                ...userData,
                isImpersonated: !!originalUser,
            };
        }
        default:
            return state;
        }
    },
    language: (state = initialState.language, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case SessionTypeKeys.SET_LANGUAGE:
            return action.payload;
        default:
            return state;
        }
    },
    sessionInfo: (state = initialState.sessionInfo, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case constants.SET_CSRF_TOKEN:
            return {
                ...state,
                csrfToken: action.payload,
            };
        case constants.SET_SIGNED_WEBSOCKET_URL:
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
        }
    },
    marketplaceProperties: (state = initialState.marketplaceProperties, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case SessionTypeKeys.SET_MARKETPLACE_PROPERTIES:
            return action.payload;
        default:
            return state;
        }
    },
    originalUser: (state = null, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case SessionTypeKeys.SET_SESSION_DATA:
            return action.payload.originalUser || null;
        default:
            return state;
        }
    },
    impersonationType: (state = null, action: ActionWithOptionalPayload) => {
        switch (action.type) {
        case SessionTypeKeys.SET_SESSION_DATA:
            return action.payload.impersonationType || null;
        default:
            return state;
        }
    },

});

export type CustomPrivileges = {
    customUserRoleIds: number[];
    name: string;
}
